import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  User, MapPin, GraduationCap, Briefcase, 
  FileText, Globe, Upload, Building2, 
  Mail, Phone, Save, Search 
} from 'lucide-react';
import './CandidateDetailsForm.css';

const CandidateDetailsForm = () => {
  // --- STATE (UNCHANGED) ---
  const [formData, setFormData] = useState({
    personalEmail: '',
    fullName: '',
    phoneNumber: '',
    permanentStreet: '',
    permanentCity: '',
    permanentState: '',
    permanentPincode: '',
    permanentCountry: '',
    collegeName: '',
    collegeRegNo: '', 
    rollNo: '',
    universityName: '',
    collegeStreet: '',
    collegeCity: '',
    collegeState: '',
    collegePincode: '',
    collegeCountry: '',
    currentCGPA: '',
    tenthPercentile: '',
    tenthBoard: '',
    twelfthPercentile: '',
    twelfthBoard: '',
    twelfthStream: '',
    citizenship: '',
    skills: '',
    totalExperience: '',
    relevantExperience: '',
    currentCTC: '',
    expectedCTC: '',
    linkedinURL: '',
    isServingNotice: false,
    lastWorkingDay: '',
    noticePeriod: '',
    isAbroadOpportunity: false,
    passportNumber: '',
  });
  const [resumeFile, setResumeFile] = useState(null);
  
  // Suggestions State
  const [collegeSuggestions, setCollegeSuggestions] = useState([]);
  const [regNoSuggestions, setRegNoSuggestions] = useState([]); 

  const navigate = useNavigate();
  
  // --- LOGIC (UNCHANGED) ---
  const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  const fetchSuggestions = async (query) => {
    if (query.length < 2) { setCollegeSuggestions([]); return; }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/colleges?query=${query}`);
      setCollegeSuggestions(response.data);
    } catch (error) { console.error('Failed to fetch college suggestions:', error); }
  };

  const fetchRegNoSuggestions = async (query) => {
    if (query.length < 1) { setRegNoSuggestions([]); return; }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/college-reg-nos?query=${query}`);
      setRegNoSuggestions(response.data);
    } catch (error) { console.error('Failed to fetch reg no suggestions:', error); }
  };

  const debouncedFetchSuggestions = debounce(fetchSuggestions, 300);
  const debouncedFetchRegNo = debounce(fetchRegNoSuggestions, 300);

  const handleCollegeNameChange = (e) => {
    const { value } = e.target;
    setFormData(prevData => ({ ...prevData, collegeName: value }));
    debouncedFetchSuggestions(value);
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData(prevData => ({ ...prevData, collegeName: suggestion }));
    setCollegeSuggestions([]);
  };

  const handleRegNoChange = (e) => {
    const { value } = e.target;
    setFormData(prevData => ({ ...prevData, collegeRegNo: value }));
    debouncedFetchRegNo(value);
  };

  const handleRegNoClick = (suggestionObj) => {
    setFormData(prevData => ({ 
        ...prevData, 
        collegeRegNo: suggestionObj.collegeRegNo,
        collegeName: suggestionObj.collegeName 
    }));
    setRegNoSuggestions([]);
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;
    if (name === 'isServingNotice' || name === 'isAbroadOpportunity') {
      newValue = value === 'true';
    }
    setFormData(prevData => ({ ...prevData, [name]: newValue }));
  };

  const handleFileChange = (e) => {
    setResumeFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userId = localStorage.getItem('userId');
    if (!userId) { alert("Error: User ID not found. Please log in again."); return; }

    const data = new FormData();
    for (const key in formData) { data.append(key, formData[key]); }
    data.append('userId', userId);
    if (resumeFile) { data.append('resumeFile', resumeFile); }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/submit-details`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(response.data.message);
      navigate('/candidate-dashboard'); 
    } catch (error) {
      console.error('Submission failed:', error.response?.data || error.message);
      alert('Form submission failed: ' + (error.response?.data?.message || 'Please ensure all required fields are filled correctly.'));
    }
  };

  return (
    <div id="candidate-form-page">
      <div className="form-background-overlay"></div>
      
      <div className="premium-form-wrapper">
        <div className="form-header">
          <h1>Professional Profile</h1>
          <p>Complete your registration to access premium placements.</p>
        </div>

        <form onSubmit={handleSubmit} className="premium-form-grid">
          
          {/* 1. PERSONAL INFO */}
          <div className="form-section-card">
            <div className="section-title-row">
              <User className="icon-brand" size={18} />
              <h3>Personal Information</h3>
            </div>
            <div className="fields-grid">
              <div className="input-group">
                <label>Personal Email *</label>
                <div className="icon-input">
                  <Mail size={16} />
                  <input type="email" name="personalEmail" value={formData.personalEmail} onChange={handleChange} required placeholder="email@example.com" />
                </div>
              </div>
              <div className="input-group">
                <label>Full Name *</label>
                <div className="icon-input">
                  <User size={16} />
                  <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required placeholder="Full Name" />
                </div>
              </div>
              <div className="input-group full-span">
                <label>Phone Number *</label>
                <div className="icon-input">
                  <Phone size={16} />
                  <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} required placeholder="+91 98765 43210" />
                </div>
              </div>
            </div>
          </div>

          {/* 2. PERMANENT ADDRESS */}
          <div className="form-section-card">
            <div className="section-title-row">
              <MapPin className="icon-brand" size={18} />
              <h3>Permanent Address</h3>
            </div>
            <div className="fields-grid">
              <div className="input-group full-span"><label>Street *</label><input type="text" name="permanentStreet" value={formData.permanentStreet} onChange={handleChange} required /></div>
              <div className="input-group"><label>City *</label><input type="text" name="permanentCity" value={formData.permanentCity} onChange={handleChange} required /></div>
              <div className="input-group"><label>State *</label><input type="text" name="permanentState" value={formData.permanentState} onChange={handleChange} required /></div>
              <div className="input-group"><label>Pincode *</label><input type="text" name="permanentPincode" value={formData.permanentPincode} onChange={handleChange} required /></div>
              <div className="input-group"><label>Country *</label><input type="text" name="permanentCountry" value={formData.permanentCountry} onChange={handleChange} required /></div>
            </div>
          </div>

          {/* 3. EDUCATION (Highlight Section) */}
          <div className="form-section-card highlight-border">
            <div className="section-title-row">
              <GraduationCap className="icon-brand" size={18} />
              <h3>Educational Information</h3>
            </div>
            <div className="fields-grid">
              
              {/* SMART SEARCH: REG NO */}
              <div className="input-group search-container full-span">
                <label>College Registration No (Auto-Link) *</label>
                <div className="icon-input">
                  <Search size={16} />
                  <input 
                    type="text" 
                    name="collegeRegNo" 
                    value={formData.collegeRegNo} 
                    onChange={handleRegNoChange} 
                    required 
                    autoComplete="off" 
                    placeholder="Type to search (e.g. JK_001)..." 
                  />
                </div>
                {regNoSuggestions.length > 0 && (
                  <ul className="smart-suggestions">
                    {regNoSuggestions.map((item, index) => ( 
                      <li key={index} onClick={() => handleRegNoClick(item)}>
                        <strong>{item.collegeRegNo}</strong> <span>({item.collegeName})</span>
                      </li> 
                    ))}
                  </ul>
                )}
              </div>

              {/* SMART SEARCH: COLLEGE NAME */}
              <div className="input-group search-container full-span">
                <label>College Name *</label>
                <div className="icon-input">
                  <Building2 size={16} />
                  <input type="text" name="collegeName" value={formData.collegeName} onChange={handleCollegeNameChange} required autoComplete="off" />
                </div>
                {collegeSuggestions.length > 0 && (
                  <ul className="smart-suggestions">
                    {collegeSuggestions.map((s, i) => ( <li key={i} onClick={() => handleSuggestionClick(s)}>{s}</li> ))}
                  </ul>
                )}
              </div>

              <div className="input-group"><label>Roll No *</label><input type="text" name="rollNo" value={formData.rollNo} onChange={handleChange} required /></div>
              <div className="input-group full-span"><label>University Name *</label><input type="text" name="universityName" value={formData.universityName} onChange={handleChange} required /></div>
              
              {/* MARKS */}
              <div className="input-group"><label>Current CGPA *</label><input type="number" step="0.01" name="currentCGPA" value={formData.currentCGPA} onChange={handleChange} required placeholder="8.5" /></div>
              <div className="input-group"><label>10th % / CGPA *</label><input type="text" name="tenthPercentile" value={formData.tenthPercentile} onChange={handleChange} required /></div>
              <div className="input-group"><label>10th Board *</label><input type="text" name="tenthBoard" value={formData.tenthBoard} onChange={handleChange} required /></div>
              <div className="input-group"><label>12th % / CGPA *</label><input type="text" name="twelfthPercentile" value={formData.twelfthPercentile} onChange={handleChange} required /></div>
              <div className="input-group"><label>12th Board *</label><input type="text" name="twelfthBoard" value={formData.twelfthBoard} onChange={handleChange} required /></div>
              <div className="input-group full-span"><label>12th Stream *</label><input type="text" name="twelfthStream" value={formData.twelfthStream} onChange={handleChange} required /></div>
            </div>
          </div>

          {/* 4. COLLEGE ADDRESS */}
          <div className="form-section-card">
            <div className="section-title-row">
              <MapPin className="icon-brand" size={18} />
              <h3>College Address</h3>
            </div>
            <div className="fields-grid">
              <div className="input-group full-span"><label>Street *</label><input type="text" name="collegeStreet" value={formData.collegeStreet} onChange={handleChange} required /></div>
              <div className="input-group"><label>City *</label><input type="text" name="collegeCity" value={formData.collegeCity} onChange={handleChange} required /></div>
              <div className="input-group"><label>State *</label><input type="text" name="collegeState" value={formData.collegeState} onChange={handleChange} required /></div>
              <div className="input-group"><label>Pincode *</label><input type="text" name="collegePincode" value={formData.collegePincode} onChange={handleChange} required /></div>
              <div className="input-group full-span"><label>Country *</label><input type="text" name="collegeCountry" value={formData.collegeCountry} onChange={handleChange} required /></div>
            </div>
          </div>

          {/* 5. EXPERIENCE */}
          <div className="form-section-card">
            <div className="section-title-row">
              <Briefcase className="icon-brand" size={18} />
              <h3>Experience & CTC</h3>
            </div>
            <div className="fields-grid">
              <div className="input-group"><label>Total Exp (Yrs)</label><input type="text" name="totalExperience" value={formData.totalExperience} onChange={handleChange} placeholder="0" /></div>
              <div className="input-group"><label>Relevant Exp</label><input type="text" name="relevantExperience" value={formData.relevantExperience} onChange={handleChange} placeholder="0" /></div>
              <div className="input-group"><label>Current CTC (LPA) *</label><input type="text" name="currentCTC" value={formData.currentCTC} onChange={handleChange} required /></div>
              <div className="input-group"><label>Expected CTC (LPA) *</label><input type="text" name="expectedCTC" value={formData.expectedCTC} onChange={handleChange} required /></div>
            </div>
          </div>

          {/* 6. ADDITIONAL */}
          <div className="form-section-card">
            <div className="section-title-row">
              <FileText className="icon-brand" size={18} />
              <h3>Skills & Resume</h3>
            </div>
            <div className="fields-grid">
              <div className="input-group full-span"><label>Skills</label><input type="text" name="skills" value={formData.skills} onChange={handleChange} placeholder="Java, React, SQL" /></div>
              <div className="input-group full-span"><label>LinkedIn URL</label><input type="url" name="linkedinURL" value={formData.linkedinURL} onChange={handleChange} placeholder="https://linkedin.com/in/..." /></div>
              <div className="input-group full-span">
                <label>Upload Resume (PDF) *</label>
                <div className="file-box">
                  <Upload size={18} />
                  <input type="file" name="resumeFile" onChange={handleFileChange} accept=".pdf" required />
                  <span>{resumeFile ? resumeFile.name : "Click to Upload PDF"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 7. JOB STATUS & ABROAD */}
          <div className="form-section-card">
            <div className="section-title-row">
              <Globe className="icon-brand" size={18} />
              <h3>Availability</h3>
            </div>
            <div className="fields-grid">
              <div className="input-group"><label>Citizenship *</label><input type="text" name="citizenship" value={formData.citizenship} onChange={handleChange} required /></div>
              <div className="input-group">
                <label>Serving Notice? *</label>
                <select name="isServingNotice" value={formData.isServingNotice} onChange={handleChange} required>
                  <option value={false}>No</option>
                  <option value={true}>Yes</option>
                </select>
              </div>
              {formData.isServingNotice && (
                <div className="input-group full-span"><label>Last Working Day *</label><input type="date" name="lastWorkingDay" value={formData.lastWorkingDay} onChange={handleChange} required /></div>
              )}
              <div className="input-group full-span"><label>Notice Period *</label><input type="text" name="noticePeriod" value={formData.noticePeriod} onChange={handleChange} required placeholder="e.g. 30 Days" /></div>
              
              <div className="input-group full-span">
                <label>Abroad Opportunity? *</label>
                <select name="isAbroadOpportunity" value={formData.isAbroadOpportunity} onChange={handleChange} required>
                  <option value={false}>No</option>
                  <option value={true}>Yes</option>
                </select>
              </div>
              {formData.isAbroadOpportunity && (
                <div className="input-group full-span"><label>Passport Number *</label><input type="text" name="passportNumber" value={formData.passportNumber} onChange={handleChange} required /></div>
              )}
            </div>
          </div>

          <button type="submit" className="final-submit-btn">
            <Save size={20} /> Submit & Proceed
          </button>

        </form>
      </div>
    </div>
  );
};
export default CandidateDetailsForm;